/** Quick "log this food" dialog used from the Foods page. */
import { useState, type ReactElement } from 'react';
import { formatDayLong, timestampForDay } from '../lib/dates';
import { effectiveLayout, foodBlocks, sectionForMealType, storageFor } from '../lib/diaryLayout';
import { itemNutrients, roundMap } from '../lib/foodNutrients';
import { MEAL_HOUR, mealTypeForNow } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import type { DiaryBlock, MealInsert, MealItem } from '../lib/types';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Spinner } from './ui';

export function AddToDiaryModal({ item, onClose }: { item: MealItem; onClose: () => void }): ReactElement {
  const { source, day, today, toast, bump, settings } = useApp();
  const layout = effectiveLayout(settings.diary_layout, settings.energy_mode);
  const sections: DiaryBlock[] = foodBlocks(layout);
  const [sectionId, setSectionId] = useState<string>(() => sectionForMealType(layout, mealTypeForNow()).id);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    const section: DiaryBlock = sections.find((b) => b.id === sectionId) ?? sectionForMealType(layout, mealTypeForNow());
    const where = storageFor(section);
    const row: MealInsert = {
      eaten_at: day === today ? new Date().toISOString() : timestampForDay(day, MEAL_HOUR[where.meal_type]),
      local_date: day, meal_type: where.meal_type, diary_section: where.diary_section,
      description: `${item.quantity} ${item.unit} ${item.name}`.slice(0, 2000),
      source: 'usda',
      calories: item.calories, protein_g: item.protein_g, carbs_g: item.carbs_g, fat_g: item.fat_g,
      fiber_g: item.fiber_g ?? null, sugar_g: item.sugar_g ?? null, alcohol_g: item.alcohol_g ?? null,
      sodium_mg: item.sodium_mg ?? null, potassium_mg: item.potassium_mg ?? null,
      items: [item], nutrients: roundMap(itemNutrients(item)), ai_model: null, ai_confidence: null,
    };
    try {
      await source.insertMeal(row);
      toast(`Added ${Math.round(item.calories)} kcal to ${section.label}`);
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add to diary" onClose={onClose} width={460} footer={
      <>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={16} />} Add</button>
      </>
    }>
      <div className="stack" style={{ gap: 14 }}>
        <div><strong>{item.name}</strong><div className="muted num" style={{ fontSize: 13.5 }}>{item.quantity} {item.unit} · {item.grams} g · {Math.round(item.calories)} kcal</div></div>
        <label className="field"><span>Diary section</span>
          <select className="input" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            {sections.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </label>
        <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Goes to {day === today ? 'today' : formatDayLong(day)} (change the day on the Diary page).</p>
        {error !== null && <div className="note err">{error}</div>}
      </div>
    </Modal>
  );
}
